import { useState, useCallback } from 'react';
import { Upload, X, Loader2, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PdfUpload({ pdfs = [], onChange, folder = 'events/review-pdfs', maxFiles = 3 }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const canUpload = pdfs.length < maxFiles;

  const uploadFiles = async (files) => {
    const pdfFiles = files.filter(f => f.type === 'application/pdf');
    if (!pdfFiles.length) return;

    setUploading(true);
    const token = localStorage.getItem('shrt_token');
    const newPdfs = [...pdfs];

    for (const file of pdfFiles) {
      if (newPdfs.length >= maxFiles) break;

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await fetch(`${API_URL}/api/admin/upload/r2-pdf?folder=${encodeURIComponent(folder)}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        if (data.url) {
          newPdfs.push({ url: data.url, filename: data.filename, size: data.size });
        }
      } catch (err) {
        console.error('PDF upload error:', err);
      }
    }

    onChange(newPdfs);
    setUploading(false);
  };

  const handleRemove = (index) => {
    const newPdfs = [...pdfs];
    newPdfs.splice(index, 1);
    onChange(newPdfs);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  }, [pdfs, onChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileInput = (e) => {
    uploadFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      {/* Dropzone */}
      {canUpload && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer
            ${dragActive
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
          onClick={() => document.getElementById('pdf-file-input')?.click()}
        >
          <input
            id="pdf-file-input"
            type="file"
            accept="application/pdf"
            multiple
            onChange={handleFileInput}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">PDF wird hochgeladen...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className={`p-2 rounded-full ${dragActive ? 'bg-primary/10' : 'bg-muted'}`}>
                {dragActive ? (
                  <FileText className="w-5 h-5 text-primary" />
                ) : (
                  <Upload className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium">
                  {dragActive ? 'PDF hier ablegen' : 'PDF hier ablegen oder klicken'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximal {maxFiles} PDF-Dateien, je max. 10 MB
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PDF List */}
      {pdfs.length > 0 && (
        <div className="space-y-2">
          {pdfs.map((pdf, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 border border-border bg-muted/20"
            >
              <div className="w-9 h-9 bg-red-500/20 text-red-500 flex items-center justify-center text-xs font-bold flex-shrink-0">
                PDF
              </div>
              <div className="flex-1 min-w-0">
                <a
                  href={pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-foreground hover:text-primary truncate block"
                >
                  {pdf.filename || `PDF ${index + 1}`}
                </a>
                {pdf.size ? (
                  <span className="text-xs text-muted-foreground">{formatSize(pdf.size)}</span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="p-2 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                title="Entfernen"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
